import React from "react";
import { Route, Switch } from "wouter";
import { QueryClientProvider } from "@tanstack/react-query";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { AppProvider } from "@/store/AppStore";
import { queryClient } from "@/lib/queryClient";
import { Layout } from "@/components/Layout";
import { LoginPage } from "@/pages/LoginPage";
import { Dashboard } from "@/pages/Dashboard";
import { StudentsPage } from "@/pages/StudentsPage";
import { NotificationsPage } from "@/pages/NotificationsPage";
import { SessionHistoryPage } from "@/pages/SessionHistoryPage";
import MessagesPage from "@/pages/MessagesPage";
import { AgencyDashboard } from "@/pages/AgencyDashboard";
import { AgencyStudentsPage } from "@/pages/AgencyStudentsPage";
import { AgencyCounselorsPage } from "@/pages/AgencyCounselorsPage";
import { AgencySessionsPage } from "@/pages/AgencySessionsPage";
import { ProfilePage } from "@/pages/ProfilePage";
import { ResourcesPage } from "@/pages/ResourcesPage";
import { ResourceDetailPage } from "@/pages/ResourceDetailPage";
import { VideoCallManager } from "./components/video-call/VideoCallManager";
import { Toaster } from "@/components/ui/toaster";
import { NotificationManager } from "@/components/NotificationManager";
import { ChatManager } from "@/components/ChatManager";
import { ImpersonatePage } from "@/pages/Impersonate";
import { ImpersonationBanner } from "@/components/impersonation/ImpersonationBanner";

// Component to protect agency routes
const AgencyRouteGuard: React.FC<{ component: React.ComponentType }> = ({
  component: Component,
}) => {
  const { isAgencyAdmin } = useAuth();

  if (!isAgencyAdmin) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <h2 className="text-2xl font-semibold text-gray-900 mb-2">
            Access Denied
          </h2>
          <p className="text-gray-600">
            You need agency admin privileges to access this page.
          </p>
        </div>
      </div>
    );
  }

  return <Component />;
};

const AppContent: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuth();

  // The /impersonate landing page exchanges its one-time code for a session
  // itself, so it has to render before the logged-out gate below.
  if (window.location.pathname === "/impersonate") {
    return <ImpersonatePage />;
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center space-y-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <LoginPage />;
  }

  return (
    <Layout>
      <NotificationManager />
      <ChatManager />
      <Switch>
        <Route path="/" component={Dashboard} />
        <Route path="/dashboard" component={Dashboard} />
        <Route path="/students" component={StudentsPage} />
        <Route path="/messages" component={MessagesPage} />
        <Route path="/notifications" component={NotificationsPage} />
        <Route path="/sessions" component={SessionHistoryPage} />
        <Route path="/resources/:id" component={ResourceDetailPage} />
        <Route path="/resources" component={ResourcesPage} />
        <Route
          path="/agency"
          component={() => <AgencyRouteGuard component={AgencyDashboard} />}
        />
        <Route
          path="/agency/students"
          component={() => <AgencyRouteGuard component={AgencyStudentsPage} />}
        />
        <Route
          path="/agency/counselors"
          component={() => (
            <AgencyRouteGuard component={AgencyCounselorsPage} />
          )}
        />
        <Route
          path="/agency/sessions"
          component={() => <AgencyRouteGuard component={AgencySessionsPage} />}
        />
        <Route path="/profile" component={ProfilePage} />
        <Route>
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center">
              <h2 className="text-2xl font-semibold text-gray-900 mb-2">
                Page Not Found
              </h2>
              <p className="text-gray-600">
                The page you're looking for doesn't exist.
              </p>
            </div>
          </div>
        </Route>
      </Switch>
      <Toaster />
    </Layout>
  );
};

const App: React.FC = () => {

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <AppProvider>
          <VideoCallManager>
            <AppContent />
            <ImpersonationBanner />
          </VideoCallManager>
        </AppProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
};

export default App;
